import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Loader2, Check, Trash2, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Reusable AI Lifecycle Draft Modal
 *
 * Fires automatically after a Project, Goal, or Deal (moved to Won) is created
 * so the founder can review + approve an AI-drafted execution plan before any
 * workspace records are persisted. Nothing is created silently.
 *
 * Props:
 *  - trigger: "goal_created" | "project_created" | "deal_won"
 *  - entityId: id of the goal / project / deal
 *  - entityLabel: human name shown in header
 *  - open: boolean
 *  - onClose: () => void
 *  - onApproved: (result) => void  // called after approve
 */

const TRIGGER_META = {
  goal_created: {
    title: "Draft this goal's roadmap",
    subtitle: "AI proposes stages + measurable milestones for this goal.",
    draftPath: (id) => `/goals/${id}/plan`,
    approvePath: (id) => `/goals/${id}/approve-plan`,
    approveKey: "stages",
    shape: "goal",
    successMsg: "Roadmap saved to your goal.",
  },
  project_created: {
    title: "Draft this project's plan",
    subtitle: "AI proposes milestones + actionable tasks for this project.",
    draftPath: (id) => `/projects/${id}/ai-plan`,
    approvePath: (id) => `/projects/${id}/approve-plan`,
    approveKey: "milestones",
    shape: "project",
    successMsg: "Plan saved to your project.",
  },
  deal_won: {
    title: "Plan the next steps for this Won deal",
    subtitle: "AI inspects the deal and proposes the appropriate follow-through (delivery / onboarding / follow-up).",
    draftPath: (id) => `/deals/${id}/won-plan`,
    approvePath: (id) => `/deals/${id}/won-approve`,
    approveKey: "sections",
    shape: "deal_won",
    successMsg: "Next-step plan created.",
  },
};

const PRIORITY_OPTIONS = ["low", "medium", "high"];

export default function LifecycleDraftModal({ trigger, entityId, entityLabel, open, onClose, onApproved }) {
  const meta = TRIGGER_META[trigger];
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");

  const fetchDraft = useCallback(async () => {
    if (!meta || !entityId) return;
    setLoading(true);
    setError("");
    setDraft(null);
    try {
      const { data } = await api.post(meta.draftPath(entityId));
      setDraft(data.preview || {});
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate the draft.");
    } finally {
      setLoading(false);
    }
  }, [meta, entityId]);

  useEffect(() => {
    if (open && entityId && !draft && !loading) fetchDraft();
    // reset when closed
    if (!open) {
      setDraft(null);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId]);

  const handleApprove = async () => {
    if (!draft) return;
    setApproving(true);
    try {
      const body = { [meta.approveKey]: draft[meta.approveKey] || [] };
      const { data } = await api.post(meta.approvePath(entityId), body);
      toast.success(meta.successMsg);
      onApproved?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the plan.");
    } finally {
      setApproving(false);
    }
  };

  if (!open || !meta) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="lifecycle-draft-modal">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
              <Sparkles className="h-3.5 w-3.5" /> AI draft — review before approving
            </div>
            <h2 className="mt-1 text-lg font-semibold text-foreground">{meta.title}</h2>
            {entityLabel && <p className="text-sm text-muted-foreground">{entityLabel}</p>}
            <p className="mt-1 text-xs text-muted-foreground">{meta.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
            data-testid="lifecycle-draft-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="lifecycle-draft-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> AI is drafting your plan…
            </div>
          )}
          {error && !loading && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
              <button onClick={fetchDraft} className="ml-2 underline">Retry</button>
            </div>
          )}
          {draft && !loading && (
            <DraftEditor shape={meta.shape} draft={draft} onChange={setDraft} />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            data-testid="lifecycle-draft-dismiss"
          >
            Dismiss
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchDraft}
              disabled={loading || approving}
              className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
              data-testid="lifecycle-draft-regenerate"
            >
              Regenerate
            </button>
            <button
              onClick={handleApprove}
              disabled={approving || loading || !draft}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-50"
              data-testid="lifecycle-draft-approve"
            >
              {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Approve &amp; create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Draft editors per shape                                             */
/* ------------------------------------------------------------------ */

function DraftEditor({ shape, draft, onChange }) {
  if (shape === "goal") return <GoalDraft draft={draft} onChange={onChange} />;
  if (shape === "project") return <ProjectDraft draft={draft} onChange={onChange} />;
  if (shape === "deal_won") return <DealWonDraft draft={draft} onChange={onChange} />;
  return null;
}

/* --- GOAL --------------------------------------------------------- */
function GoalDraft({ draft, onChange }) {
  const stages = draft.stages || [];
  const updateStage = (i, changes) => {
    const next = stages.map((s, idx) => (idx === i ? { ...s, ...changes } : s));
    onChange({ ...draft, stages: next });
  };
  const removeStage = (i) => onChange({ ...draft, stages: stages.filter((_, idx) => idx !== i) });
  const removeMilestone = (si, mi) => {
    const st = stages[si];
    const nextMs = (st.milestones || []).filter((_, idx) => idx !== mi);
    updateStage(si, { milestones: nextMs });
  };
  const updateMilestone = (si, mi, changes) => {
    const st = stages[si];
    const nextMs = (st.milestones || []).map((m, idx) => (idx === mi ? { ...m, ...changes } : m));
    updateStage(si, { milestones: nextMs });
  };
  if (!stages.length) return <EmptyDraft label="No stages were drafted." />;
  return (
    <div className="space-y-4" data-testid="draft-goal">
      {stages.map((s, i) => (
        <div key={s.id || i} className="rounded-xl border border-border p-3">
          <div className="flex items-center gap-2">
            <input
              value={s.name || ""}
              onChange={(e) => updateStage(i, { name: e.target.value })}
              className="flex-1 rounded-lg border border-border bg-transparent px-2 py-1 text-sm font-semibold"
              placeholder="Stage name"
            />
            <button onClick={() => removeStage(i)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Remove stage">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          {s.description && <p className="mt-1 text-xs text-muted-foreground">{s.description}</p>}
          <div className="mt-2 space-y-2">
            {(s.milestones || []).map((m, mi) => (
              <div key={m.id || mi} className="flex items-center gap-2 rounded-lg bg-muted/40 px-2 py-1.5">
                <input
                  value={m.name || ""}
                  onChange={(e) => updateMilestone(i, mi, { name: e.target.value })}
                  className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-sm focus:border-border"
                  placeholder="Milestone"
                />
                <button onClick={() => removeMilestone(i, mi)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Remove milestone">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* --- PROJECT ------------------------------------------------------ */
function ProjectDraft({ draft, onChange }) {
  const ms = draft.milestones || [];
  const updateMs = (i, changes) => {
    const next = ms.map((m, idx) => (idx === i ? { ...m, ...changes } : m));
    onChange({ ...draft, milestones: next });
  };
  const removeMs = (i) => onChange({ ...draft, milestones: ms.filter((_, idx) => idx !== i) });
  const updateTask = (i, ti, changes) => {
    const m = ms[i];
    const next = (m.tasks || []).map((t, idx) => (idx === ti ? { ...t, ...changes } : t));
    updateMs(i, { tasks: next });
  };
  const removeTask = (i, ti) => {
    const m = ms[i];
    updateMs(i, { tasks: (m.tasks || []).filter((_, idx) => idx !== ti) });
  };
  const addTask = (i) => {
    const m = ms[i];
    updateMs(i, { tasks: [...(m.tasks || []), { title: "New task", priority: "medium" }] });
  };
  if (!ms.length) return <EmptyDraft label="No milestones were drafted." />;
  return (
    <div className="space-y-4" data-testid="draft-project">
      {ms.map((m, i) => (
        <div key={m.temp_id || i} className="rounded-xl border border-border p-3">
          <div className="flex items-center gap-2">
            <input
              value={m.name || ""}
              onChange={(e) => updateMs(i, { name: e.target.value })}
              className="flex-1 rounded-lg border border-border bg-transparent px-2 py-1 text-sm font-semibold"
              placeholder="Milestone"
            />
            <button onClick={() => removeMs(i)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Remove milestone">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          {m.description && <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>}
          <div className="mt-2 space-y-1.5">
            {(m.tasks || []).map((t, ti) => (
              <div key={t.temp_id || ti} className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/40 px-2 py-1.5">
                <input
                  value={t.title || ""}
                  onChange={(e) => updateTask(i, ti, { title: e.target.value })}
                  className="flex-1 min-w-[180px] rounded-md border border-transparent bg-transparent px-2 py-1 text-sm focus:border-border"
                  placeholder="Task"
                />
                <select
                  value={t.priority || "medium"}
                  onChange={(e) => updateTask(i, ti, { priority: e.target.value })}
                  className="rounded-md border border-border bg-transparent px-2 py-1 text-xs"
                >
                  {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <button onClick={() => removeTask(i, ti)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Remove task">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button onClick={() => addTask(i)} className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted">
              <Plus className="h-3 w-3" /> Add task
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* --- DEAL WON ----------------------------------------------------- */
function DealWonDraft({ draft, onChange }) {
  const sections = draft.sections || [];
  const updateSec = (i, changes) => {
    const next = sections.map((s, idx) => (idx === i ? { ...s, ...changes } : s));
    onChange({ ...draft, sections: next });
  };
  const removeSec = (i) => onChange({ ...draft, sections: sections.filter((_, idx) => idx !== i) });
  const updateTask = (i, ti, changes) => {
    const s = sections[i];
    const next = (s.tasks || []).map((t, idx) => (idx === ti ? { ...t, ...changes } : t));
    updateSec(i, { tasks: next });
  };
  const removeTask = (i, ti) => {
    const s = sections[i];
    updateSec(i, { tasks: (s.tasks || []).filter((_, idx) => idx !== ti) });
  };
  const addTask = (i) => {
    const s = sections[i];
    updateSec(i, { tasks: [...(s.tasks || []), { title: "New action", priority: "medium" }] });
  };
  if (!sections.length) return <EmptyDraft label="AI did not propose any next steps." />;
  return (
    <div className="space-y-4" data-testid="draft-deal-won">
      {draft.summary && <p className="text-xs italic text-muted-foreground">{draft.summary}</p>}
      {sections.map((s, i) => (
        <div key={s.temp_id || i} className="rounded-xl border border-border p-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
              {s.kind || "next-step"}
            </span>
            <input
              value={s.title || ""}
              onChange={(e) => updateSec(i, { title: e.target.value })}
              className="flex-1 rounded-lg border border-border bg-transparent px-2 py-1 text-sm font-semibold"
              placeholder="Section title"
            />
            <button onClick={() => removeSec(i)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Remove section">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          {s.reason && <p className="mt-1 text-xs text-muted-foreground">{s.reason}</p>}
          <div className="mt-2 space-y-1.5">
            {(s.tasks || []).map((t, ti) => (
              <div key={t.temp_id || ti} className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/40 px-2 py-1.5">
                <input
                  value={t.title || ""}
                  onChange={(e) => updateTask(i, ti, { title: e.target.value })}
                  className="flex-1 min-w-[180px] rounded-md border border-transparent bg-transparent px-2 py-1 text-sm focus:border-border"
                  placeholder="Task"
                />
                <select
                  value={t.priority || "medium"}
                  onChange={(e) => updateTask(i, ti, { priority: e.target.value })}
                  className="rounded-md border border-border bg-transparent px-2 py-1 text-xs"
                >
                  {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <button onClick={() => removeTask(i, ti)} className="rounded-md p-1 text-muted-foreground hover:text-destructive" aria-label="Remove task">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button onClick={() => addTask(i)} className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted">
              <Plus className="h-3 w-3" /> Add action
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyDraft({ label }) {
  return <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{label}</div>;
}
